<template>
  <Teleport to="body">
    <div class="fab-dock">
      <span class="fab-dock__glow" aria-hidden="true"></span>
      <button type="button" class="fab" @click="open">
        <SfIcon name="newNote" :size="19" :stroke-width="2" />
        <span>{{ $t("newNote.title") }}</span>
      </button>
    </div>

    <Transition name="new-note-modal">
      <div
        v-if="isOpen"
        class="new-note__backdrop"
        role="dialog"
        aria-modal="true"
        @click.self="close"
      >
        <div class="new-note__sheet" :class="{ 'new-note__sheet--wide': step !== 'menu' }">
          <header class="new-note__header">
            <button
              v-if="step !== 'menu'"
              type="button"
              class="new-note__back"
              :aria-label="$t('notes.back')"
              @click="goBackToMenu"
            >
              <SfIcon name="chevronLeft" :size="18" />
            </button>
            <h2 class="new-note__title">{{ headerTitle }}</h2>
            <button
              type="button"
              class="new-note__close"
              :aria-label="$t('common.dismiss', 'Close')"
              @click="close"
            >
              <SfIcon name="close" :size="16" />
            </button>
          </header>

          <div v-if="step === 'menu'" class="new-note__menu">
            <button
              v-for="option in options"
              :key="option.id"
              type="button"
              class="new-note__option"
              :disabled="!!option.disabled"
              @click="onPick(option)"
            >
              <span class="new-note__option-icon" aria-hidden="true">
                <img :src="option.art" alt="" width="26" height="26" />
              </span>
              <span class="new-note__option-body">
                <span class="new-note__option-title">{{ option.title }}</span>
                <span class="new-note__option-hint">{{ option.hint }}</span>
              </span>
            </button>
          </div>

          <div v-else-if="step === 'lesson'" class="new-note__step">
            <label class="new-note__label" for="new-note-lesson-context">
              {{ $t("newNote.lesson.contextLabel") }}
            </label>
            <!-- Pictures and documents pasted below, above the text as on
                 the phone's composer. -->
            <ul v-if="lessonAttachments.length > 0" class="new-note__filelist">
              <li v-for="item in lessonAttachments" :key="item.id">
                <span v-if="item.loading" class="new-note__file-loading" aria-hidden="true"></span>
                <img
                  v-else
                  class="new-note__file-art"
                  :class="{ 'new-note__file-art--thumb': !!item.preview }"
                  :src="item.preview || fileArt(item.file.name)"
                  :alt="extLabel(item.file)"
                  width="32"
                  height="32"
                />
                <span class="new-note__filename" :title="item.file ? item.file.name : ''">
                  {{ item.file ? item.file.name : "" }}
                </span>
                <span v-if="item.file" class="new-note__filesize">{{ formatBytes(item.file.size) }}</span>
                <button
                  type="button"
                  class="new-note__file-remove"
                  :aria-label="$t('newNote.attachments.remove')"
                  @click="removeLessonAttachment(item.id)"
                >
                  <SfIcon name="close" :size="14" />
                </button>
              </li>
            </ul>
            <textarea
              id="new-note-lesson-context"
              v-model="contextText"
              class="new-note__textarea"
              :placeholder="$t('newNote.lesson.contextPlaceholder')"
              rows="6"
              @paste="onLessonPaste"
              @dragover="onLessonDragOver"
              @drop="onLessonDrop"
            ></textarea>
            <button
              type="button"
              class="new-note__primary"
              :disabled="!canSubmitLesson"
              @click="submitLesson"
            >
              <span v-if="busy" class="new-note__spinner" aria-hidden="true"></span>
              <span>{{ $t("newNote.generate") }}</span>
            </button>
          </div>

          <div v-else-if="step === 'youtube'" class="new-note__step">
            <a
              v-if="previewVideoId"
              class="yt-preview"
              :href="`https://www.youtube.com/watch?v=${previewVideoId}`"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span class="yt-preview__thumb">
                <img
                  :src="`https://i.ytimg.com/vi/${previewVideoId}/hqdefault.jpg`"
                  :alt="previewTitle || 'YouTube'"
                  loading="lazy"
                />
                <span class="yt-preview__play" aria-hidden="true">▶</span>
              </span>
              <span class="yt-preview__body">
                <span class="yt-preview__brand">
                  <span class="yt-preview__brand-icon" aria-hidden="true">YT</span>
                  <span>YouTube</span>
                </span>
                <span class="yt-preview__title">{{ previewTitle || "…" }}</span>
              </span>
            </a>

            <label class="new-note__label" for="new-note-youtube-url">
              {{ $t("newNote.youtube.urlLabel") }}
            </label>
            <input
              id="new-note-youtube-url"
              v-model="youtubeUrl"
              type="url"
              class="new-note__input"
              placeholder="https://www.youtube.com/watch?v=…"
              autocomplete="off"
            />
            <button
              type="button"
              class="new-note__primary"
              :disabled="busy || !isYoutubeUrlValid"
              @click="submitYoutube"
            >
              <span v-if="busy" class="new-note__spinner" aria-hidden="true"></span>
              <span>{{ $t("newNote.generate") }}</span>
            </button>
          </div>

          <div v-else-if="step === 'import'" class="new-note__step">
            <label class="new-note__label">
              {{ $t("newNote.import.label") }}
            </label>
            <label
              class="new-note__filepicker"
              :class="{ 'new-note__filepicker--dragover': isDraggingOver }"
              @dragenter.prevent="isDraggingOver = true"
              @dragover.prevent="isDraggingOver = true"
              @dragleave.prevent="isDraggingOver = false"
              @drop.prevent="onDropFiles"
            >
              <input
                type="file"
                multiple
                :accept="importAccept"
                class="new-note__file-input"
                @change="onPickFiles"
              />
              <img class="new-note__filepicker-icon" :src="openFolderArt" alt="" aria-hidden="true" />
              <span class="new-note__filepicker-cta">{{ $t("newNote.import.choose") }}</span>
              <span class="new-note__filepicker-hint">
                {{ $t("newNote.import.hint") }}
              </span>
            </label>
            <ul v-if="pickedFiles.length > 0" class="new-note__filelist">
              <li v-for="(file, idx) in pickedFiles" :key="file.name + idx">
                <img
                  class="new-note__file-art"
                  :src="fileArt(file.name)"
                  :alt="extLabel(file)"
                  width="32"
                  height="32"
                />
                <span class="new-note__filename" :title="file.name">{{ file.name }}</span>
                <span class="new-note__filesize">{{ formatBytes(file.size) }}</span>
                <button
                  type="button"
                  class="new-note__file-remove"
                  :aria-label="$t('common.dismiss', 'Remove')"
                  @click="removeFile(idx)"
                >
                  <SfIcon name="close" :size="14" />
                </button>
              </li>
            </ul>
            <textarea
              v-model="contextText"
              class="new-note__textarea new-note__textarea--small"
              :placeholder="$t('newNote.import.contextPlaceholder')"
              rows="3"
            ></textarea>
            <button
              type="button"
              class="new-note__primary"
              :disabled="busy || pickedFiles.length === 0"
              @click="submitImport"
            >
              <span v-if="busy" class="new-note__spinner" aria-hidden="true"></span>
              <span>{{ $t("newNote.generate") }}</span>
            </button>
          </div>

          <p v-if="transcriptionStatus" class="new-note__status" role="status">
            {{ transcriptionStatus }}
          </p>
          <p v-if="errorMessage" class="new-note__error" role="alert">
            {{ errorMessage }}
          </p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import {
  createGeneratedNote,
  createSimpleNote,
} from "../stores/noteCreationStore";
import { dashboardUiState } from "../stores/notesStore";
import { showSnack } from "../stores/snackbarStore";
import {
  LIMIT_MESSAGE,
  MAX_LESSON_ATTACHMENTS,
  admitPastedDocuments,
  dragCarriesFiles,
  pastedAttachments,
  pastedImageAsJpeg,
  uploadValidationError,
} from "../utils/lessonAttachments";
import SfIcon from "./SfIcon.vue";
import { ART, fileArt } from "../assets";
import {
  isAudioFile,
  transcribeAudioFile,
  transcriptFile,
} from "../utils/audioTranscriber";
import { IMPORT_ACCEPT, isImportable } from "../utils/importFileTypes";

// Refusals stay up longer than the limit notice, as on iOS (4.5 s).
const LONG_MESSAGE_KEYS = ["unsupported", "tooLarge"];

let nextAttachmentId = 1;

/**
 * Extracts a YouTube video ID from any of the supported URL shapes
 * (`youtube.com/watch?v=…`, `youtu.be/…`, `/shorts/…`, `/embed/…`).
 * Mirrors the iOS `youtubeVideoID(from:)` in VideoLessonInput.swift.
 */
function extractYoutubeVideoId(raw) {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  let url;
  try {
    url = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`);
  } catch (_) {
    return null;
  }
  const host = (url.hostname || "").toLowerCase();
  const validate = (id) => {
    if (!id) return null;
    const candidate = id.trim();
    if (!candidate) return null;
    const stripped = candidate.replace(/[-_]/g, "");
    return /^[A-Za-z0-9]{6,}$/.test(stripped) ? candidate : null;
  };
  if (host.includes("youtu.be")) {
    const segs = url.pathname.split("/").filter(Boolean);
    return validate(segs[0]);
  }
  if (!host.includes("youtube.com") && !host.includes("youtube-nocookie.com")) {
    return null;
  }
  const v = url.searchParams.get("v");
  if (v) return validate(v);
  const segs = url.pathname.split("/").filter(Boolean);
  const shortsIdx = segs.indexOf("shorts");
  if (shortsIdx >= 0 && segs[shortsIdx + 1]) return validate(segs[shortsIdx + 1]);
  const embedIdx = segs.indexOf("embed");
  if (embedIdx >= 0 && segs[embedIdx + 1]) return validate(segs[embedIdx + 1]);
  return null;
}

export default {
  name: "NewNoteFab",
  components: { SfIcon },
  data() {
    return {
      openFolderArt: ART.openFolder,
      isOpen: false,
      step: "menu", // 'menu' | 'lesson' | 'youtube' | 'import'
      contextText: "",
      youtubeUrl: "",
      previewVideoId: null,
      previewTitle: "",
      previewTitleAbort: null,
      pickedFiles: [],
      // The lesson's attachments, pasted into its text:
      // { id, file, preview (object URL of a picture), loading }.
      lessonAttachments: [],
      busy: false,
      errorMessage: "",
      // What the recordings being transcribed are at, shown under the form.
      transcriptionStatus: "",
      isDraggingOver: false,
    };
  },
  beforeUnmount() {
    this.clearLessonAttachments();
  },
  watch: {
    youtubeUrl(next) {
      const id = extractYoutubeVideoId(next);
      if (id !== this.previewVideoId) {
        this.previewVideoId = id;
        this.previewTitle = "";
        if (id) this.fetchYoutubeTitle(id);
      }
    },
  },
  computed: {
    /** The import picker: documents, pictures, and recordings (transcribed). */
    importAccept() {
      return IMPORT_ACCEPT;
    },
    options() {
      return [
        {
          id: "lesson",
          art: ART.chatBubble,
          title: this.$t("newNote.options.lesson.title"),
          hint: this.$t("newNote.options.lesson.hint"),
          step: "lesson",
        },
        // YouTube generation is temporarily hidden from the menu — re-enable
        // by uncommenting this entry. The `youtube` step + handler in this
        // component are intentionally kept intact so deep-links and the
        // existing logic still work when the option is restored.
        // {
        //   id: "youtube",
        //   emoji: "▶️",
        //   title: this.$t("newNote.options.youtube.title"),
        //   hint: this.$t("newNote.options.youtube.hint"),
        //   step: "youtube",
        // },
        {
          id: "import",
          art: ART.folders,
          title: this.$t("newNote.options.import.title"),
          hint: this.$t("newNote.options.import.hint"),
          step: "import",
        },
        {
          id: "simple",
          art: ART.note,
          title: this.$t("newNote.options.simple.title"),
          hint: this.$t("newNote.options.simple.hint"),
          action: () => this.submitSimple(),
        },
      ];
    },
    headerTitle() {
      switch (this.step) {
        case "lesson":
          return this.$t("newNote.options.lesson.title");
        case "youtube":
          return this.$t("newNote.options.youtube.title");
        case "import":
          return this.$t("newNote.options.import.title");
        default:
          return this.$t("newNote.title");
      }
    },
    /** Text, or attachments, and none of them still being prepared. */
    canSubmitLesson() {
      if (this.busy || this.lessonAttachments.some((a) => a.loading)) return false;
      return this.contextText.trim().length > 0 || this.lessonAttachments.length > 0;
    },
    isYoutubeUrlValid() {
      // We rely on the extracted video ID — that catches more URL shapes
      // (youtu.be, /shorts/, /embed/, …) than a flat regex would.
      return !!this.previewVideoId;
    },
  },
  methods: {
    open() {
      this.resetForm();
      this.isOpen = true;
    },
    close() {
      this.isOpen = false;
      this.resetForm();
    },
    resetForm() {
      this.step = "menu";
      this.contextText = "";
      this.youtubeUrl = "";
      this.previewVideoId = null;
      this.previewTitle = "";
      if (this.previewTitleAbort) {
        try { this.previewTitleAbort.abort(); } catch (_) { /* ignore */ }
        this.previewTitleAbort = null;
      }
      this.pickedFiles = [];
      this.clearLessonAttachments();
      this.busy = false;
      this.errorMessage = "";
    },
    /**
     * Mirrors VideoLessonInput.swift's `fetchYouTubeTitle` — uses YouTube's
     * public oEmbed endpoint (no API key needed) to read the video's title.
     * The latest in-flight call is aborted whenever a new video id arrives.
     */
    async fetchYoutubeTitle(videoId) {
      if (this.previewTitleAbort) {
        try { this.previewTitleAbort.abort(); } catch (_) { /* ignore */ }
      }
      const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      this.previewTitleAbort = controller;
      const url =
        "https://www.youtube.com/oembed?format=json&url=" +
        encodeURIComponent(`https://www.youtube.com/watch?v=${videoId}`);
      try {
        const res = await fetch(url, { signal: controller && controller.signal });
        if (!res.ok) return;
        const data = await res.json();
        // Make sure the response still matches the latest URL the user typed
        // — they may have already moved on to a different video.
        if (this.previewVideoId !== videoId) return;
        const title = (data && data.title ? String(data.title) : "").trim();
        if (title) this.previewTitle = title;
      } catch (err) {
        if (err && err.name === "AbortError") return;
        // oEmbed may 401 on private/embedding-disabled videos. Fall back to
        // the placeholder ("…") rather than blocking the user.
      } finally {
        if (this.previewTitleAbort === controller) this.previewTitleAbort = null;
      }
    },
    goBackToMenu() {
      this.step = "menu";
      this.errorMessage = "";
    },
    onPick(option) {
      this.errorMessage = "";
      if (option.action) {
        option.action();
        return;
      }
      this.step = option.step;
    },
    onPickFiles(event) {
      const list = event.target.files ? Array.from(event.target.files) : [];
      this.addImportFiles(list);
      // Allow re-selecting the same file later by clearing the input value.
      event.target.value = "";
    },
    onDropFiles(event) {
      this.isDraggingOver = false;
      const list = event.dataTransfer && event.dataTransfer.files
        ? Array.from(event.dataTransfer.files)
        : [];
      this.addImportFiles(list);
    },
    /** Only what the apps import too; the first file turned away says so. */
    addImportFiles(list) {
      const accepted = list.filter(isImportable);
      const refused = list.find((file) => !isImportable(file));
      this.errorMessage = refused
        ? this.$t("newNote.unsupportedFile", { name: refused.name })
        : "";
      if (accepted.length > 0) this.pickedFiles = [...this.pickedFiles, ...accepted];
    },
    removeFile(idx) {
      this.pickedFiles.splice(idx, 1);
    },
    /**
     * A paste into the lesson's text (ImagePasteEnabler): text pastes as
     * text; pictures and documents become attachments instead.
     */
    onLessonPaste(event) {
      const pasted = pastedAttachments(event.clipboardData);
      if (!pasted) return;
      event.preventDefault();
      // Pictures first, then documents, as the phone takes them.
      this.addPastedImages(pasted.images);
      this.addPastedDocuments(pasted.documents);
    },
    /** Files dragged over the text are taken on drop; left to the browser,
     *  they would open in place of the page and lose the lesson. */
    onLessonDragOver(event) {
      if (!dragCarriesFiles(event.dataTransfer)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
    },
    /** A drop on the text is a paste (the phone's paste delegate takes
     *  both): dropped text is still text. */
    onLessonDrop(event) {
      const data = event.dataTransfer;
      if (!dragCarriesFiles(data)) return;
      const dropped = pastedAttachments(data);
      if (!dropped && data.getData("text/plain")) return;
      // Files that are no attachment are not opened either.
      event.preventDefault();
      if (!dropped) return;
      this.addPastedImages(dropped.images);
      this.addPastedDocuments(dropped.documents);
    },
    showAttachmentMessage(message) {
      showSnack(this.$t(`newNote.attachments.${message.key}`, message.params), {
        duration: LONG_MESSAGE_KEYS.includes(message.key) ? 4500 : undefined,
      });
    },
    /** `appendPastedImages`: each picture in a free slot, as a JPEG. */
    addPastedImages(images) {
      if (!images.length) return;
      const free = MAX_LESSON_ATTACHMENTS - this.lessonAttachments.length;
      if (free <= 0) {
        this.showAttachmentMessage(LIMIT_MESSAGE);
        return;
      }
      // Each slot is held while its picture is turned into a JPEG.
      images.slice(0, free).forEach((image) => {
        const id = nextAttachmentId++;
        this.lessonAttachments.push({ id, file: null, preview: null, loading: true });
        pastedImageAsJpeg(image).catch(() => null).then((jpeg) => {
          const index = this.lessonAttachments.findIndex((a) => a.id === id);
          // Taken off, or the composer closed, meanwhile.
          if (index < 0) return;
          const error = jpeg && uploadValidationError(jpeg.name, jpeg.type, jpeg.size);
          if (!jpeg || error) {
            this.lessonAttachments.splice(index, 1);
            if (error) this.showAttachmentMessage(error);
            return;
          }
          const preview = typeof URL.createObjectURL === "function" ? URL.createObjectURL(jpeg) : null;
          this.lessonAttachments.splice(index, 1, { id, file: jpeg, preview, loading: false });
        });
      });
    },
    /** `appendPastedFiles`: documents as they are, within the limits. */
    addPastedDocuments(documents) {
      if (!documents.length) return;
      const { accepted, messages } = admitPastedDocuments(this.lessonAttachments.length, documents);
      messages.forEach((message) => this.showAttachmentMessage(message));
      for (const file of accepted) {
        this.lessonAttachments.push({ id: nextAttachmentId++, file, preview: null, loading: false });
      }
    },
    removeLessonAttachment(id) {
      const index = this.lessonAttachments.findIndex((a) => a.id === id);
      if (index < 0) return;
      const [removed] = this.lessonAttachments.splice(index, 1);
      if (removed.preview) URL.revokeObjectURL(removed.preview);
    },
    clearLessonAttachments() {
      for (const item of this.lessonAttachments) {
        if (item.preview) URL.revokeObjectURL(item.preview);
      }
      this.lessonAttachments = [];
    },
    fileArt,
    extLabel(file) {
      const name = file && file.name ? String(file.name) : "";
      const dot = name.lastIndexOf(".");
      if (dot < 0) return "FILE";
      return name.slice(dot + 1).slice(0, 4).toUpperCase();
    },
    formatBytes(bytes) {
      if (bytes == null || isNaN(bytes)) return "";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    activeFolder() {
      return dashboardUiState.activeFolder || "";
    },
    async runCreate(promise) {
      this.busy = true;
      this.errorMessage = "";
      try {
        const noteID = await promise;
        showSnack(this.$t("newNote.created"), { variant: "success" });
        this.close();
        this.$router.push(`/note/${noteID}`);
      } catch (err) {
        console.error("create note failed:", err);
        if (err.code === "unsupported-file") {
          this.errorMessage = this.$t("newNote.unsupportedFile", { name: err.fileName });
        } else if (err.code === "file-too-large") {
          this.errorMessage = this.$t("newNote.fileTooLarge", { name: err.fileName });
        } else if (err.code === "upload-failed") {
          this.errorMessage = this.$t("newNote.uploadFailed");
        } else if (err.code === "audio-empty") {
          this.errorMessage = this.$t("newNote.audio.empty", { name: err.fileName });
        } else if (err.code === "audio-unreadable" || err.code === "audio-failed") {
          this.errorMessage = this.$t("newNote.audio.failed", { name: err.fileName });
        } else {
          this.errorMessage = err.message || this.$t("newNote.failed");
        }
      } finally {
        this.busy = false;
      }
    },
    submitSimple() {
      this.runCreate(createSimpleNote({ folder: this.activeFolder() }));
    },
    submitLesson() {
      if (!this.canSubmitLesson) return;
      const text = this.contextText.trim();
      const files = this.lessonAttachments.map((a) => a.file).filter(Boolean);
      this.runCreate(
        createGeneratedNote({
          noteType: "LESSON",
          folder: this.activeFolder(),
          context: text,
          files,
        }),
      );
    },
    submitYoutube() {
      const url = this.youtubeUrl.trim();
      if (!this.isYoutubeUrlValid) return;
      this.runCreate(
        createGeneratedNote({
          noteType: "VIDEO",
          folder: this.activeFolder(),
          context: url,
          maxPages: 5,
        }),
      );
    },
    submitImport() {
      if (this.pickedFiles.length === 0) return;
      this.runCreate(
        this.withRecordingsAsText(this.pickedFiles).then((files) =>
          createGeneratedNote({
            noteType: "IMPORT",
            folder: this.activeFolder(),
            context: this.contextText.trim(),
            files,
          }),
        ),
      );
    },
    /**
     * Recordings among `files`, transcribed here and replaced by their
     * text — as on iOS, only the words leave the page.
     */
    async withRecordingsAsText(files) {
      const recordings = files.filter(isAudioFile);
      if (recordings.length === 0) return files;
      this.busy = true;
      const language = (this.$i18n?.locale || "").split("-")[0] || null;
      const replaced = new Map();
      try {
        for (const [index, file] of recordings.entries()) {
          const position = recordings.length > 1 ? ` (${index + 1}/${recordings.length})` : "";
          this.transcriptionStatus = this.$t("newNote.audio.preparing") + position;
          const text = await transcribeAudioFile(file, {
            language,
            onProgress: ({ phase, progress }) => {
              this.transcriptionStatus = (phase === "loading"
                ? this.$t("newNote.audio.downloading", { percent: Math.round(progress || 0) })
                : this.$t("newNote.audio.transcribing", { name: file.name })) + position;
            },
          });
          console.log(`[audio] transcription of "${file.name}" (${text.length} chars):\n${text}`);
          replaced.set(file, transcriptFile(file, text));
        }
      } finally {
        this.transcriptionStatus = "";
      }
      return files.map((file) => replaced.get(file) || file);
    },
  },
};
</script>

<style scoped>
/* ========== Floating Action Button ========== */
/* The iOS "New note" pill: translucent white capsule over the brand's
   blurred cyan → pink → yellow glow, bottom centre. */
.fab-dock {
  position: fixed;
  left: 50%;
  bottom: 28px;
  z-index: 1100;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.fab-dock__glow {
  position: absolute;
  inset: -6px -18px;
  border-radius: 999px;
  background: linear-gradient(90deg, #21b4f3, #ff44f9, #ffcc00);
  background-size: 200% 100%;
  filter: blur(22px);
  opacity: 0.75;
  animation: fab-glow 3s ease-in-out infinite alternate;
  pointer-events: none;
}

@keyframes fab-glow {
  from {
    background-position: 0% 50%;
  }
  to {
    background-position: 100% 50%;
  }
}

.fab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 15px 24px;
  border-radius: 24px;
  border: 1px solid #ffffff;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  color: #000000;
  font: inherit;
  font-size: 16px;
  font-weight: 650;
  cursor: pointer;
  transition: transform 0.12s ease-out;
}

.fab:hover {
  transform: scale(1.04);
}

.fab:active {
  transform: scale(1.1);
}

.fab:focus-visible {
  outline: 2px solid var(--wn-accent);
  outline-offset: 4px;
}

@media (prefers-reduced-motion: reduce) {
  .fab-dock__glow {
    animation: none;
  }
  .fab,
  .fab:hover,
  .fab:active {
    transition: none;
    transform: none;
  }
}

/* ========== Modal backdrop & sheet ========== */
.new-note__backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(5, 8, 14, 0.62);
  backdrop-filter: blur(14px) saturate(120%);
  -webkit-backdrop-filter: blur(14px) saturate(120%);
}

.new-note__sheet {
  width: 100%;
  max-width: 440px;
  max-height: 90dvh;
  display: flex;
  flex-direction: column;
  background: var(--app-surface);
  border: 1px solid color-mix(in srgb, var(--app-text) 14%, transparent);
  border-radius: 24px;
  box-shadow:
    0 36px 80px -28px rgba(0, 0, 0, 0.65),
    0 8px 24px -10px rgba(0, 0, 0, 0.4);
  overflow: hidden;
}

.new-note__sheet--wide {
  max-width: 500px;
}

.new-note__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid color-mix(in srgb, var(--app-text) 8%, transparent);
}

.new-note__title {
  margin: 0;
  flex: 1;
  font-size: 17px;
  font-weight: 700;
  color: var(--app-heading);
  letter-spacing: -0.01em;
}

.new-note__back,
.new-note__close {
  width: 34px;
  height: 34px;
  border: 1px solid color-mix(in srgb, var(--app-text) 10%, transparent);
  border-radius: 999px;
  background: color-mix(in srgb, var(--app-text) 5%, transparent);
  color: var(--app-text);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, transform 0.1s ease;
}

.new-note__back:hover,
.new-note__close:hover {
  background: color-mix(in srgb, var(--app-text) 12%, transparent);
}

.new-note__back:active,
.new-note__close:active {
  transform: scale(0.94);
}

/* ========== Menu (option cards) ========== */
.new-note__menu {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  overflow-y: auto;
}

.new-note__option {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid color-mix(in srgb, var(--app-text) 8%, transparent);
  border-radius: 16px;
  background: color-mix(in srgb, var(--app-bg) 50%, transparent);
  text-align: left;
  cursor: pointer;
  color: var(--app-text);
  transition: transform 0.15s ease, background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.new-note__option:hover:not(:disabled) {
  transform: translateY(-1px);
  background: color-mix(in srgb, var(--app-text) 7%, transparent);
  border-color: color-mix(in srgb, var(--app-text) 22%, transparent);
  box-shadow: 0 14px 30px -22px rgba(0, 0, 0, 0.6);
}

.new-note__option:active:not(:disabled) {
  transform: translateY(0);
}

.new-note__option:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* iOS ActionButton: the illustration in a yellow 20% capsule. */
.new-note__option-icon {
  width: 60px;
  height: 42px;
  border-radius: 999px;
  background: rgba(255, 204, 0, 0.2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.new-note__option-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
}

.new-note__option-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--app-heading);
  letter-spacing: -0.005em;
}

.new-note__option-hint {
  font-size: 12.5px;
  color: var(--app-text-muted);
  line-height: 1.4;
}

/* ========== Step screens ========== */
.new-note__step {
  padding: 18px 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
}

.new-note__label {
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--app-text-muted);
}

.new-note__textarea,
.new-note__input {
  /* Without `border-box`, `width: 100%` + horizontal padding + 1px border
     makes the textarea ~30px wider than its parent, which pushes its right
     edge past the modal's right padding and looks off-center. */
  box-sizing: border-box;
  width: 100%;
  padding: 13px 14px;
  border: 1px solid color-mix(in srgb, var(--app-text) 12%, transparent);
  border-radius: 14px;
  background: color-mix(in srgb, var(--app-bg) 65%, transparent);
  color: var(--app-text);
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.new-note__textarea {
  min-height: 120px;
  line-height: 1.5;
}

.new-note__textarea--small {
  min-height: 70px;
}

.new-note__textarea:focus,
.new-note__input:focus {
  outline: none;
  border-color: rgba(255, 226, 14, 0.7);
  background: color-mix(in srgb, var(--app-bg) 80%, transparent);
  box-shadow: 0 0 0 4px rgba(255, 226, 14, 0.18);
}

.new-note__textarea::placeholder,
.new-note__input::placeholder {
  color: color-mix(in srgb, var(--app-text-muted) 80%, transparent);
}

/* ========== File picker ========== */
.new-note__filepicker {
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 28px 20px;
  border: 1.5px dashed color-mix(in srgb, var(--app-text) 18%, transparent);
  border-radius: 16px;
  cursor: pointer;
  text-align: center;
  background: color-mix(in srgb, var(--app-bg) 50%, transparent);
  transition: background-color 0.18s ease, border-color 0.18s ease, transform 0.15s ease;
}

.new-note__filepicker:hover {
  background: color-mix(in srgb, var(--app-text) 5%, transparent);
  border-color: color-mix(in srgb, var(--app-text) 28%, transparent);
}

.new-note__filepicker--dragover {
  background: rgba(255, 226, 14, 0.08);
  border-color: rgba(255, 226, 14, 0.65);
  border-style: solid;
  transform: scale(1.01);
}

.new-note__filepicker-icon {
  width: 52px;
  height: 52px;
  object-fit: contain;
  margin-bottom: 4px;
  transition: transform 0.15s ease;
}

.new-note__filepicker--dragover .new-note__filepicker-icon {
  transform: scale(1.12);
}

.new-note__file-art {
  width: 32px;
  height: 32px;
  object-fit: contain;
  flex-shrink: 0;
}

/* A pasted picture shows itself rather than a file icon. */
.new-note__file-art--thumb {
  object-fit: cover;
  border-radius: 6px;
}

/* A pasted picture still being turned into a JPEG. */
.new-note__file-loading {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.new-note__file-loading::before {
  content: "";
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid color-mix(in srgb, var(--app-text) 25%, transparent);
  border-top-color: var(--app-text);
  animation: new-note-spin 0.8s linear infinite;
}

.new-note__file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.new-note__filepicker-cta {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--app-heading);
  letter-spacing: -0.005em;
}

.new-note__filepicker-hint {
  font-size: 12px;
  color: var(--app-text-muted);
}

.new-note__filelist {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 6px;
}

.new-note__filelist li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px 8px 8px;
  border: 1px solid color-mix(in srgb, var(--app-text) 8%, transparent);
  border-radius: 12px;
  background: color-mix(in srgb, var(--app-bg) 60%, transparent);
}

.new-note__filename {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--app-text);
}

.new-note__filesize {
  font-size: 11.5px;
  color: var(--app-text-muted);
  flex-shrink: 0;
}

.new-note__file-remove {
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--app-text-muted);
  cursor: pointer;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.new-note__file-remove:hover {
  background: color-mix(in srgb, var(--app-text) 12%, transparent);
  color: var(--app-text);
}

/* ========== YouTube preview card ========== */
.yt-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border: 1px solid color-mix(in srgb, var(--app-text) 8%, transparent);
  border-radius: 14px;
  background: color-mix(in srgb, var(--app-bg) 60%, transparent);
  text-decoration: none;
  color: inherit;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.yt-preview:hover {
  background: color-mix(in srgb, var(--app-text) 6%, transparent);
  border-color: color-mix(in srgb, var(--app-text) 18%, transparent);
}

.yt-preview__thumb {
  position: relative;
  width: 120px;
  height: 68px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
  background: color-mix(in srgb, var(--app-text) 10%, transparent);
}

.yt-preview__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.yt-preview__play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 22px;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.45);
  pointer-events: none;
}

.yt-preview__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.yt-preview__brand {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: var(--app-heading);
  letter-spacing: -0.005em;
}

.yt-preview__brand-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 16px;
  border-radius: 4px;
  background: #ff0033;
  color: #ffffff;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.yt-preview__title {
  font-size: 12.5px;
  color: var(--app-text-muted);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ========== Primary CTA ========== */
.new-note__primary {
  margin-top: 4px;
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 18px;
  border: 0;
  border-radius: 999px;
  background: #ffe20e;
  color: #1a1a1a;
  font-size: 14.5px;
  font-weight: 700;
  letter-spacing: -0.005em;
  cursor: pointer;
  box-shadow: 0 12px 24px -16px rgba(0, 0, 0, 0.5);
  transition: transform 0.15s ease, box-shadow 0.18s ease, filter 0.15s ease;
}

.new-note__primary:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.04);
  box-shadow: 0 16px 32px -14px rgba(0, 0, 0, 0.55);
}

.new-note__primary:active:not(:disabled) {
  transform: translateY(0);
}

.new-note__primary:disabled {
  background: color-mix(in srgb, var(--app-text) 12%, transparent);
  color: color-mix(in srgb, var(--app-text-muted) 85%, transparent);
  box-shadow: none;
  cursor: not-allowed;
}

.new-note__spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(0, 0, 0, 0.25);
  border-top-color: #1a1a1a;
  animation: new-note-spin 0.8s linear infinite;
}

@keyframes new-note-spin {
  to {
    transform: rotate(360deg);
  }
}

/* A recording being transcribed: where it is at. */
.new-note__status {
  margin: 0 20px 12px;
  padding: 10px 12px;
  font-size: 13px;
  color: inherit;
  opacity: 0.8;
  background: rgba(127, 127, 127, 0.1);
  border-radius: 10px;
}

.new-note__error {
  margin: 0 20px 20px;
  padding: 10px 12px;
  font-size: 13px;
  color: #fca5a5;
  background: rgba(248, 113, 113, 0.08);
  border: 1px solid rgba(248, 113, 113, 0.25);
  border-radius: 10px;
}

/* ========== Modal transition ========== */
.new-note-modal-enter-active,
.new-note-modal-leave-active {
  transition: opacity 0.22s ease;
}

.new-note-modal-enter-active .new-note__sheet,
.new-note-modal-leave-active .new-note__sheet {
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.2s ease;
}

.new-note-modal-enter-from,
.new-note-modal-leave-to {
  opacity: 0;
}

.new-note-modal-enter-from .new-note__sheet,
.new-note-modal-leave-to .new-note__sheet {
  opacity: 0;
  transform: translateY(24px) scale(0.96);
}

@media (max-width: 540px) {
  .fab-dock {
    bottom: 18px;
  }
  .new-note__sheet {
    max-height: 95dvh;
  }
}

@media (prefers-reduced-motion: reduce) {
  .fab,
  .new-note__option,
  .new-note__primary,
  .new-note__filepicker {
    transition: none;
  }
  .new-note__spinner,
  .new-note__file-loading::before {
    animation: none;
  }
}
</style>
